import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { sxCircularProgressCenter, sxDarkGreyBody, sxDivider, sxSubTitle } from "../../../../../assets/styles/CommonSxProps";
import LoadingApp from "../../../../../components/LoadingApp";
import ModalFeedBack from "../../../../../components/ModalFeedBack";
import ModalNewAddress from "../../../../../components/ModalNewAddress";
import ModalNewEmail from "../../../../../components/ModalNewEmail";
import { Address } from "../../../../../interfaces/entities/Cancelamento/PassoCartaCancelamento/Address";
import { EnderecoCancelamento } from "../../../../../interfaces/entities/Cancelamento/PassoCartaCancelamento/EnderecoCancelamento";
import { EnderComunicCancelamento } from "../../../../../interfaces/entities/Cancelamento/PassoMotivoCancelamento/EnderComunicCancelamento";
import { StepCartaCancelamentoProps } from "../../../../../interfaces/props/Cancelamento/StepCartaCancelamentoProps";
import { cadastrarEmail, cadastrarEnderecoPostal } from "../../../../../services/BeneficiarioService";
import { sxFormControlLabel } from "../../../PermissoesVisualizacao/sxProps";
import { sxButton } from "../sxProps";
import './styles.css';
import { sxRadioGroup } from "./sxProps";
import TabelaEndereco from "./TabelaEndereco";
import { useAccessibility } from "../../../../../Store/contexts/AccessibilityContext";

const StepCartaCancelamento: React.FC<StepCartaCancelamentoProps> = ({ onProceed, backStep, addPassoCartaCancelamentoEmail, addPassoCartaCancelamentoAddress, passoCartaCancelamento, loading }) => {

  const [loadingApp, setLoadingApp] = useState<boolean>(false);
  const [formaEnvioSelecionada, setFormaEnvioSelecionada] = useState<'EMAIL' | 'POSTAL'>('EMAIL');
  const [enderComunicSelecionado, setEnderComunicSelecionado] = useState<EnderComunicCancelamento>();
  const emailSelecionado = enderComunicSelecionado?.enderecoComunic;
  const [enderPostalSelecionado, setEnderPostalSelecionado] = useState<EnderecoCancelamento>();
  const [idEnderPostalSelecionado, setIdEnderPostalSelecionado] = useState<number>();
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [showModalEmail, setShowModalEmail] = useState<boolean>(false);
  const [showModalNewAddress, setShowModalNewAddress] = useState<boolean>(false);

  const formRef = useRef<HTMLDivElement>(null);
  const prosseguirRef = useRef<HTMLButtonElement>(null);
  
  const { textStyleAccessibility, contrastColorAccessibility, buttonStyleAccessibility, buttonTextStyleAccessibility } = useAccessibility();
  const disableProceedButton = (formaEnvioSelecionada === 'EMAIL' && !emailSelecionado) || (formaEnvioSelecionada === 'POSTAL' && !enderPostalSelecionado);



  useEffect(() => {
    formRef?.current?.focus();
  }, []);

  useEffect(() => {
    if (!showModalEmail && !showModalNewAddress) {
      prosseguirRef?.current?.focus();
    }
  }, [showModalEmail, showModalNewAddress]);

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const openModalSuccess = (title: string, subtitle: string, onProceed: (email?: string, postal?: Address) => void) => {
    setModalFeedback({
      icon: 'success',
      title: title,
      subTitle: subtitle,
      showButtonModal: false,
      closeAction: () => {
        onProceed();
        setModalFeedback(null);
      }
    });
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormaEnvioSelecionada(event.target.value as 'EMAIL' | 'POSTAL');
    setEnderPostalSelecionado(undefined);
    setIdEnderPostalSelecionado(undefined);
    setEnderComunicSelecionado(undefined)
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>, emailSelecionado: EnderComunicCancelamento) => {
    setEnderComunicSelecionado(emailSelecionado);
  };

  const handleShowModalEmail = () => {
    setShowModalEmail(showModalEmail => !showModalEmail);
  }

  const handleShowModalNewAddress = () => {
    setShowModalNewAddress(showModalNewAddress => !showModalNewAddress);
  }

  const addEmail = async (email: string) => {
    handleShowModalEmail();
    setLoadingApp(true);
    const response = await cadastrarEmail(email);
    if (response?.status === 200) {
      openModalSuccess('E-mail cadastrado com sucesso!', '', () => addPassoCartaCancelamentoEmail(email));
    } else {
      const errorMessage = !!response?.data.mensagem ? response.data.mensagem : 'Tente novamente em instantes';
      openModalError('Erro', errorMessage);
      console.error(response);
    }
    setLoadingApp(false);
  }

  const addAddress = async (addressData: Address) => {
    handleShowModalNewAddress();
    setLoadingApp(true);
    const response = await cadastrarEnderecoPostal(addressData);
    if (response?.status === 200) {
      openModalSuccess('Endereço cadastrado', 'Seu endereço foi cadastrado com sucesso!', () => addPassoCartaCancelamentoAddress(addressData));
    } else {
      const errorMessage = !!response?.data.mensagem ? response.data.mensagem : 'Tente novamente em instantes';
      openModalError('Erro', errorMessage);
      console.error(response);
    }
    setLoadingApp(false);
  }

  const toggleEnderPostal = (index: number) => {
    const enderSelecionado = passoCartaCancelamento?.lstEndereco.at(index)!;
    setEnderPostalSelecionado(enderSelecionado);
    setIdEnderPostalSelecionado(enderSelecionado?.idEnderPostal);
  }

  const handleProceedExistingEmailOrPostalAddress = () => {
    let postalAddress = buildPostalAddress();
    onProceed(formaEnvioSelecionada, emailSelecionado, postalAddress);
  }

  const buildPostalAddress = () => {
    if (!enderPostalSelecionado) {
      return undefined;
    } else {
      const { cep, logradouro, numero, complemento, bairro } = enderPostalSelecionado;
      const postalAddress: Address = {
        cep: cep.cep,
        logradouro,
        complemento,
        bairro,
        numero
      };
      return postalAddress;
    }
  }

  const FormaEnvioEmail: React.FC = () => {
    return (
      <>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <Typography style={textStyleAccessibility} sx={sxSubTitle}>Selecione um e-mail</Typography>
          {!passoCartaCancelamento?.lstEmail?.length &&
            <Typography sx={sxDarkGreyBody} style={textStyleAccessibility} maxWidth={'650px'}>
              Você ainda não possui um e-mail cadastrado.
            </Typography>}
          {passoCartaCancelamento?.lstEmail.map((email, index) => (
            <FormControlLabel
              sx={sxFormControlLabel}
              style={textStyleAccessibility}
              key={index}
              value={email}
              control={
                <Checkbox
                  onChange={(e) => handleCheckChange(e, email)}
                  style={contrastColorAccessibility}
                  checked={(enderComunicSelecionado?.idEnderecoComunicacao === email.idEnderecoComunicacao)}
                />}
              label={email.enderecoComunic}
            />
          ))}
        </FormControl>
        <button className="underlined-text-button" style={{...textStyleAccessibility, ...contrastColorAccessibility}} onClick={handleShowModalEmail}>
          Cadastrar novo e-mail
        </button>
      </>
    )
  }

  const FormaEnvioPostal: React.FC = () => {
    return (
      <>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <Typography id={'formaEnvio'} style={textStyleAccessibility} sx={sxSubTitle}>Selecione um endereço</Typography>
          <TabelaEndereco data={passoCartaCancelamento?.lstEndereco}
            toggleIdEnderecoSelecionado={toggleEnderPostal} idEnderSelecionado={idEnderPostalSelecionado}
          />
        </FormControl>
        <button  style={{...textStyleAccessibility, ...contrastColorAccessibility}} className="underlined-text-button" onClick={handleShowModalNewAddress}>
          Cadastrar novo endereço
        </button>
      </>
    )
  }

  return (
    <>
      <LoadingApp showLoading={loadingApp} text={'Carregando...'} />
      <div className="motivo-cancelamento-container">
        <Typography style={textStyleAccessibility} sx={sxDarkGreyBody} maxWidth={'650px'}>
          Após a efetivação do processo de cancelamento, você receberá uma Carta de Cancelamento.
        </Typography>
        {loading ? <CircularProgress sx={sxCircularProgressCenter} /> :
          <>
            <FormControl component={"fieldset"} fullWidth>
              <Typography style={textStyleAccessibility} sx={sxSubTitle}>Selecione a forma de recebimento</Typography>
              <RadioGroup
                sx={sxRadioGroup}
                row
                defaultValue={'EMAIL'}
                onChange={handleRadioChange}
              >
                {passoCartaCancelamento?.lstFormaEnvio
                  .filter(formaEnvio => !!formaEnvio.value)
                  .map((formaEnvio, index) => (
                    <FormControlLabel
                      key={index}
                      ref={index === 0 ? formRef : null}
                      value={formaEnvio.value}
                      control={<Radio style={contrastColorAccessibility}/>}
                      label={formaEnvio.descricao}
                    />
                ))}
              </RadioGroup>
            </FormControl>
            <Divider sx={sxDivider} />
            {formaEnvioSelecionada === 'EMAIL' &&
              <FormaEnvioEmail />
            }
            {formaEnvioSelecionada === 'POSTAL' &&
              <FormaEnvioPostal />
            }
          </>}
        <div className='cancellation-buttons-container'>
          <Button
            variant="contained"
            size="large"
            onClick={handleProceedExistingEmailOrPostalAddress}
            disabled={disableProceedButton}
            style={buttonStyleAccessibility}
            sx={sxButton}
            ref={prosseguirRef}
          >
            <Typography style={buttonTextStyleAccessibility}>PROSSEGUIR</Typography>
          </Button>
          <button style={{...textStyleAccessibility}} className="back-button" onClick={backStep}>
            <FontAwesomeIcon style={textStyleAccessibility} icon={faChevronLeft} />&nbsp;&nbsp;<u>Voltar</u>
          </button>
        </div>
      </div>
      <ModalNewAddress showModal={showModalNewAddress} handleShowModal={handleShowModalNewAddress}
        onSubmit={addAddress} openModalFeedBack={openModalError}
      />
      <ModalNewEmail showModal={showModalEmail} handleShowModal={handleShowModalEmail} handleAddEmail={addEmail} />
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default StepCartaCancelamento;